import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { getUserReport, getManagedUsers } from "../../services/TimeTracking/api";
import { AppUser } from "../../services/TimeTracking/types";
import { transformEntriesToReport } from "../../utils/transformEntries"; // path to the function
import { useAuth } from "../../context/Auth/AuthContext";
import { exportToExcel } from "../../utils/exportToExcel";
import isoWeek from "dayjs/plugin/isoWeek"; // ensures week starts on Monday
dayjs.extend(isoWeek);

export const useReport = () => {
  const [selectedUser, setSelectedUser] = useState<AppUser | any>(null);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { userInfo } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const startOfWeek = dayjs().startOf("isoWeek"); // Monday
  const endOfWeek = dayjs().endOf("isoWeek");     // Sunday
  const [dateRange, setDateRange] = useState<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }>({
    startDate: startOfWeek,
    endDate: endOfWeek,
  });

  const [errorNotification, setErrorNotification] = useState<string | null>(null);

const dismissNotification = () => setErrorNotification(null);

  useEffect(() => {
    if (userInfo && !selectedUser) {
      const user: AppUser = {
        id: userInfo?.appUserId || userInfo?.user_id,
        firstName: userInfo?.given_name,
        lastName: userInfo?.family_name,
        email: userInfo?.email,
        photo: userInfo?.photo || userInfo?.picture,
      }
      setSelectedUser(user);
    }
  }, [userInfo]);
  
  useEffect(() => {
    if (
      selectedUser &&
      dateRange.startDate &&
      dateRange.endDate &&
      report === null &&
      !loading
    ) {
      generateReport();
    }
  }, [selectedUser, dateRange, report, loading]);
  // Load managed users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      const data = await getManagedUsers();
      setUsers(data);
    };
    fetchUsers();
  }, []);
  
  const download = async (report: any) => {
    const hoursSheet = Object.entries(report).map(([date, val]) => ({
      Date: date,
      Worked: `${(val as { hrs: number }).hrs.toFixed(2)}`,
    }));
  
    const logSheet = Object.entries(report).flatMap(([date, val]) =>
      (val as { entries: any[] }).entries.map((entry: any) => ({
        Date: date,
        Time: entry.time,
        Type: entry.type,
        Accumulated: entry.accumulated,
        Duration: entry.duration || "--",
        id: entry.id
      }))
    );
  
    await exportToExcel(
      [
        { sheetName: 'Hours', data: hoursSheet },
        { sheetName: 'Log', data: logSheet },
      ],
      `Timetracker_Report_${dayjs().format('YYYY-MM-DD')}`
    );
  };
  const generateReport = async () => {
    if (!selectedUser || !dateRange.startDate || !dateRange.endDate) return;
    setLoading(true);
    try {
      const rawEntries: any = await getUserReport(selectedUser.id, [
        dateRange.startDate.format("YYYY-MM-DD"),
        dateRange.endDate.format("YYYY-MM-DD"),

      ]);

      // console.log("📦 Raw entries from API:");
      // rawEntries.forEach((entry: any, index: number) => {
      //   console.log(`🔹 Entry ${index + 1}:`, entry);
      // });
      const structured = transformEntriesToReport(rawEntries);
      setReport(structured);
    } catch (err) {
      console.error("❌ Failed to fetch report:", err);
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    selectedUser,
    setSelectedUser,
    dateRange,
    setDateRange,
    report,
    generateReport,
    download,
    loading,
    errorNotification,
    dismissNotification
  };
};
